/**
 * voidMothershipFsm.ts
 * Top-level phase state machine for the World 2 boss (Void Mothership).
 *
 * Sibling of `bossRam.ts` / `bossDroneSpawner.ts` / `bossMissileBarrage.ts`
 * — extracted so the entity itself stays "data + delegation" and lives
 * under the 300-line file budget (AGENTS.md, Phase 5 Tech Debt).
 *
 * Drives the top-level `VoidMothershipState` machine:
 *
 *     ENTRY ── x ≤ cameraX + holdOffset ──▶ HOLD  (seed cooldowns + shield + ram)
 *     HOLD  ── hp ≤ 0 (via takeDamage)   ──▶ DEFEATED
 *
 * Per-tick HOLD work (camera-lock, weapon cooldowns, SHIELDED ↔ EXPOSED
 * cycle) and the always-ticking renderer state (shieldAngle rotation,
 * hitFlashTimer decay) live here too. The ram sub-FSM and the
 * weapon-cooldown reload (turret / drone / missile) stay where they
 * already are — `bossRam.ts` and `bossSystem.ts` respectively — this
 * stepper only owns the boss's *own* state.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * @module voidMothershipFsm
 */

import { spaceState } from './spaceState';
import Logger from '../utils/logger';
import type { VoidMothership } from '../entities/VoidMothership';
import {
  VOID_MOTHERSHIP_HOLD_X_OFFSET,
  VOID_MOTHERSHIP_HOLD_Y,
  VOID_MOTHERSHIP_BOB_AMPLITUDE_PX,
  VOID_MOTHERSHIP_BOB_FREQ_HZ,
  VOID_MOTHERSHIP_SWEEP_AMPLITUDE_PX,
  VOID_MOTHERSHIP_SWEEP_FREQ_HZ,
  VOID_MOTHERSHIP_TURRET_RAMP_IN_S,
  VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S,
  VOID_MOTHERSHIP_SHIELDED_DURATION_S,
  VOID_MOTHERSHIP_EXPOSED_DURATION_S,
  VOID_MOTHERSHIP_SHIELD_RAMP_IN_S,
  VOID_MOTHERSHIP_SHIELD_ROTATION_RAD_S,
  VOID_MOTHERSHIP_RAM_RAMP_IN_S,
  VOID_MOTHERSHIP_MISSILE_RAMP_IN_S,
} from '../../constants/bossConstants';

/**
 * Advance the boss's top-level phase machine one fixed timestep.
 *
 * Safe no-op when the boss is not active. Caller is `VoidMothership.update`
 * (entity delegation) — `bossSystem.updateBoss` reaches this transitively.
 *
 * @param boss - The live boss entity (caller ensures non-null).
 * @param dt - Fixed timestep in seconds.
 */
export function stepVoidMothership(boss: VoidMothership, dt: number): void {
  if (!boss.active) return;

  // DECISION: Shield-ring rotation accumulates every tick regardless
  // of state. Renderer gates on `isShielded && state === 'HOLD'`, so
  // ticking unconditionally costs one add + one mod per frame and
  // guarantees the ring picks up from its last on-screen angle when
  // SHIELDED reopens (instead of snapping to zero each window).
  boss.shieldAngle =
    (boss.shieldAngle + VOID_MOTHERSHIP_SHIELD_ROTATION_RAD_S * dt) %
    (Math.PI * 2);

  // Hit-flash decay runs every tick regardless of state so a flash
  // landed mid-CHARGE still fades on the same schedule as a HOLD hit.
  if (boss.hitFlashTimer > 0) {
    boss.hitFlashTimer -= dt;
    if (boss.hitFlashTimer < 0) boss.hitFlashTimer = 0;
  }

  if (boss.state === 'ENTRY') {
    stepEntry(boss, dt);
  } else if (boss.state === 'HOLD') {
    stepHold(boss, dt);
  }
  // Y is anchored at VOID_MOTHERSHIP_HOLD_Y during ENTRY; HOLD then
  // bobs the boss vertically about that anchor.
}

/**
 * ENTRY: drift left in world-space until the boss reaches the live
 * camera-relative hold anchor, then snap to HOLD and seed every
 * subordinate timer (turret / drone / missile / shield / ram).
 *
 * DECISION: Drift in world space (`vx < 0`) rather than tracking the
 * camera directly, so the apparent on-screen speed during the entry
 * slide is `entrySpeed + autoScrollSpeed`. Lock to HOLD as soon as the
 * boss reaches its target camera-relative X — using `<=` rather than
 * `<` so a single tick that overshoots the hold line cannot miss the
 * transition.
 */
function stepEntry(boss: VoidMothership, dt: number): void {
  boss.x += boss.vx * dt;
  const targetWorldX = spaceState.cameraX + VOID_MOTHERSHIP_HOLD_X_OFFSET;
  if (boss.x <= targetWorldX) {
    boss.x = targetWorldX;
    boss.vx = 0;
    boss.state = 'HOLD';
    Logger.debug('VoidMothership', 'ENTRY → HOLD (reached hold anchor)');
    // Ramp-in delay so the first turret shot doesn't fire on the
    // same tick the boss snaps to HOLD.
    boss.turretCooldown = VOID_MOTHERSHIP_TURRET_RAMP_IN_S;
    // Drone wave intentionally rides a longer ramp than the turret
    // (2.0 s vs 1.0 s) so the player parses the bolt first, then
    // the spawn second.
    boss.droneSpawnCooldown = VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S;
    // Missiles ride the longest ramp of any HOLD weapon so they
    // arrive last in the escalation sequence (turret → drone → ram
    // windup → missile barrage).
    boss.missileCooldown = VOID_MOTHERSHIP_MISSILE_RAMP_IN_S;
    // Shield opens HOLD in SHIELDED so the player reads the turret +
    // drone tells before any damage window appears. Timer is seeded
    // to the shielded-ramp constant (currently == SHIELDED_DURATION)
    // so the first cycle is no shorter than steady-state.
    boss.isShielded = true;
    boss.shieldTimer = VOID_MOTHERSHIP_SHIELD_RAMP_IN_S;
    // Ram waits longer than the shield ramp-in so the player sees a
    // full shield/expose cycle before the first windup.
    boss.ramCooldown = VOID_MOTHERSHIP_RAM_RAMP_IN_S;
    boss.ramPhase = 'IDLE';
  }
}

/**
 * HOLD: camera-lock when the ram isn't actively driving X, tick every
 * weapon cooldown, and flip the SHIELDED ↔ EXPOSED cycle when its timer
 * runs out.
 */
function stepHold(boss: VoidMothership, dt: number): void {
  // Camera-locked only while the ram isn't actively moving the boss.
  // During CHARGE / RETURN the ram stepper drives `x`, so skipping
  // the lock here is the single hand-off point.
  //
  // DECISION (W2 overhaul): Added a lateral X sweep around the hold
  // anchor so the boss is never reliably at the same column for more
  // than a few seconds. Combined with the existing Y bob this gives
  // a Lissajous path. Bob and sweep keep *independent* phase timers
  // so each one wraps at its own exact period — sharing one timer and
  // wrapping at `1 / min(freq, freq)` snapped the slower wave's phase
  // every wrap (PR #124 review).
  if (boss.ramPhase === 'IDLE' || boss.ramPhase === 'TELEGRAPH') {
    // DECISION: Sample sine at the *current* phase, then advance the
    // timer. The first HOLD tick stays at `sin(0) = 0` so the
    // camera-lock test sees an exact anchor snap, and subsequent
    // ticks pick up the swept offset cleanly.
    const sweep =
      Math.sin(boss.sweepTimer * Math.PI * 2 * VOID_MOTHERSHIP_SWEEP_FREQ_HZ) *
      VOID_MOTHERSHIP_SWEEP_AMPLITUDE_PX;
    boss.x = spaceState.cameraX + VOID_MOTHERSHIP_HOLD_X_OFFSET + sweep;
    const sweepPeriod = 1 / VOID_MOTHERSHIP_SWEEP_FREQ_HZ;
    boss.sweepTimer = (boss.sweepTimer + dt) % sweepPeriod;
  }
  // Vertical bob — only applied while the ram isn't lunging so a
  // CHARGE / RETURN keeps a stable Y for the trail / collision read.
  // DECISION: Accumulate phase seconds in `bobTimer` and wrap at the
  // bob's own period so `Math.sin` stays numerically stable and the
  // wrap is phase-clean. Bob is a function of the entity-owned
  // `bobTimer` rather than `spaceState.elapsedSeconds` so the motion
  // pauses cleanly during ENTRY / DEFEATED / pause without a state-
  // capture sidecar.
  if (boss.ramPhase === 'IDLE' || boss.ramPhase === 'TELEGRAPH') {
    // DECISION: Same sample-then-advance order as the sweep above so
    // tests that pin the first-tick y value (e.g. CHARGE freeze) still
    // see the natural starting phase.
    boss.y =
      VOID_MOTHERSHIP_HOLD_Y +
      Math.sin(boss.bobTimer * Math.PI * 2 * VOID_MOTHERSHIP_BOB_FREQ_HZ) *
        VOID_MOTHERSHIP_BOB_AMPLITUDE_PX;
    const bobPeriod = 1 / VOID_MOTHERSHIP_BOB_FREQ_HZ;
    boss.bobTimer = (boss.bobTimer + dt) % bobPeriod;
  }
  // Drive every weapon cooldown only while in HOLD. `bossSystem` fires
  // + reloads each when it crosses zero.
  if (boss.turretCooldown > 0) boss.turretCooldown -= dt;
  if (boss.droneSpawnCooldown > 0) boss.droneSpawnCooldown -= dt;
  if (boss.missileCooldown > 0) boss.missileCooldown -= dt;
  // Shield cycle is self-contained on the entity — no system reads
  // are required to flip, so the rule lives here next to the timer.
  boss.shieldTimer -= dt;
  if (boss.shieldTimer <= 0) {
    boss.isShielded = !boss.isShielded;
    boss.shieldTimer += boss.isShielded
      ? VOID_MOTHERSHIP_SHIELDED_DURATION_S
      : VOID_MOTHERSHIP_EXPOSED_DURATION_S;
  }
}
